import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { RULE_ARITHMETIC_FUNCTION_TYPES } from 'yeself-common/enum/RuleEnum';

import SelectField from '../../components/SelectField';
import PlainStringField from '../../components/PlainStringField';

import { CompletedCountRuleValues, FinishedCountRuleValues, SeenCountRuleValues } from '../types';

type CountRuleFieldProps = {
    value: SeenCountRuleValues | CompletedCountRuleValues | FinishedCountRuleValues;
    onChange: (value: SeenCountRuleValues | CompletedCountRuleValues | FinishedCountRuleValues) => void;
};

const CountRuleField = ({ value, onChange }: CountRuleFieldProps): React.ReactElement => {
    const [levelValue, setLevelValue] = useState({
        level0: value.level?.[0]?.data.key || null,
        level1: value.level?.[1]?.data.key || null,
        level2: value.level?.[2]?.data.value || null,
    });

    const handleOnChange = useCallback(
        (index: number) => (newValue) => {
            const newLevelValue = { ...levelValue, [`level${index}`]: newValue };

            const level: CountRuleFieldProps['value']['level'] = [
                newLevelValue.level0 ? { data: { key: newLevelValue.level0 } } : undefined,
                newLevelValue.level1 ? { data: { key: newLevelValue.level1 } } : undefined,
                newLevelValue.level2 ? { data: { value: newLevelValue.level2 } } : undefined,
            ];

            setLevelValue(newLevelValue);
            onChange({ ...value, level });
        },
        [levelValue, onChange, value]
    );

    return (
        <>
            <SelectField
                autoWidth
                options={[
                    {
                        label: <FormattedMessage id="Rules.level.is" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.IS,
                    },
                    {
                        label: <FormattedMessage id="Rules.level.not" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.NOT,
                    },
                ]}
                value={levelValue.level0 || ''}
                onChange={handleOnChange(0)}
            />
            {levelValue.level0 ? (
                <SelectField
                    autoWidth
                    options={[
                        {
                            label: <FormattedMessage id="Rules.level.eq" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.EQ,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.gt" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.GT,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.lt" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.LT,
                        },
                    ]}
                    value={levelValue.level1 || ''}
                    onChange={handleOnChange(1)}
                />
            ) : null}
            {levelValue.level1 ? (
                <PlainStringField value={levelValue.level2 || ''} onChange={handleOnChange(2)} />
            ) : null}
        </>
    );
};

export default CountRuleField;
